import React from "react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Crumbs } from "../components/Shared";
import { SITE } from "../data/mock";

export function LegalShell({ title, updated, crumbName, path, children }) {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: crumbName, path },
  ];
  return (
    <section className="bg-[#0A0A0B] py-14 lg:py-20">
      <div className="max-w-3xl mx-auto px-6">
        <Reveal>
          <Crumbs items={crumbs} />
          <h1 className="mt-8 font-heading font-extrabold uppercase text-4xl sm:text-5xl text-white tracking-tight">{title}</h1>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Last updated: {updated}</p>
          <div className="mt-10 space-y-8 legal-copy">{children}</div>
        </Reveal>
      </div>
    </section>
  );
}

export const H2 = ({ children }) => (
  <h2 className="font-heading font-extrabold uppercase text-xl text-white tracking-tight">{children}</h2>
);
export const P = ({ children }) => <p className="mt-3 text-[15px] text-neutral-300 leading-[1.8]">{children}</p>;
export const UL = ({ items }) => (
  <ul className="mt-3 space-y-2">
    {items.map((li) => (
      <li key={typeof li === "string" ? li.slice(0, 48) : li.key || String(li)} className="flex items-start gap-3 text-[15px] text-neutral-300 leading-relaxed">
        <span className="mt-2.5 w-1.5 h-1.5 bg-[#9D0423] shrink-0" />
        <span>{li}</span>
      </li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Privacy Policy", path: "/privacy-policy" },
  ];
  return (
    <>
      <Seo
        title="Privacy Policy | Boston Iron Grit"
        description="How Boston Iron Grit collects, uses and protects your information — including website forms, analytics, session recording and email communications."
        path="/privacy-policy"
        schemas={[breadcrumbSchema(crumbs)]}
      />
      <LegalShell title="Privacy Policy" updated="August 12, 2026" crumbName="Privacy Policy" path="/privacy-policy">
        <div>
          <H2>Who we are</H2>
          <P>
            Boston Iron Grit ("we," "us") operates the gym at {SITE.address.full} and the website bostonirongrittraining.com.
            This policy explains what information we collect through this website, how we use it, and the choices you have.
            Questions can be sent to {SITE.email} or {SITE.phone}.
          </P>
        </div>
        <div>
          <H2>Information you give us</H2>
          <P>When you submit a form on this site we collect the information you enter, which may include:</P>
          <UL
            items={[
              "Goal review requests: name, email, phone, program interest, goals, and a preferred appointment day and time.",
              "Contact messages: name, email, phone and your message.",
              "Live B.I.G. 365 app waitlist and 8-Week Challenge signups: name, email and phone.",
              "Membership hold or cancellation requests: name, email, dates and reason.",
            ]}
          />
          <P>We use this information to respond to you, schedule your visit, and administer your membership. We do not sell your personal information.</P>
        </div>
        <div>
          <H2>Analytics and session recording</H2>
          <P>
            This website uses Google Analytics (GA4) to measure how visitors use the site — pages viewed, approximate location,
            device type and events such as form submissions. It also uses PostHog, a product analytics tool, which includes
            <strong className="text-white"> session recording</strong>: PostHog may record how you interact with pages (clicks, scrolling and
            navigation) to help us improve the site. Form fields containing personal information are masked where supported. These tools set
            cookies or similar identifiers in your browser.
          </P>
          <P>
            You can opt out of analytics cookies through your browser settings, browser extensions such as the Google Analytics opt-out
            add-on, or the "Do Not Track" features of your browser.
          </P>
        </div>
        <div>
          <H2>Emails and communications</H2>
          <P>
            When you submit a form we may email or text you to follow up on your request. Transactional notification emails are delivered
            through a third-party email provider. We do not enroll you in marketing lists without your consent.
          </P>
        </div>
        <div>
          <H2>How long we keep information</H2>
          <P>
            Form submissions are retained for as long as needed to serve you and for reasonable business record-keeping. You can ask us to
            delete your information at any time by emailing {SITE.email}.
          </P>
        </div>
        <div>
          <H2>Sharing</H2>
          <P>
            We share information only with service providers who help us run the website and the gym (hosting, analytics, email delivery
            and our class booking platform, ChalkIt Pro), and where required by law. Health-related services offered through Live B.I.G.
            365 and the Gold Standard Programs are provided by licensed medical partners under their own privacy practices.
          </P>
        </div>
        <div>
          <H2>Your rights</H2>
          <P>
            You may request access to, correction of, or deletion of the personal information we hold about you. Email {SITE.email} and we
            will respond within a reasonable timeframe.
          </P>
        </div>
        <div>
          <H2>Changes to this policy</H2>
          <P>We may update this policy from time to time. The date at the top reflects the latest revision.</P>
        </div>
      </LegalShell>
    </>
  );
}
